import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gift, Users, Coins, Copy, Check, Link2, Share2, 
  Mail, Clock, ChevronRight, Sparkles, Loader2,
  CheckCircle, AlertCircle, ArrowRight, CreditCard,
  RefreshCw, Info, PartyPopper, TrendingUp, Target,
  Send, Plus, X, Inbox, Edit3
} from 'lucide-react';
import { partnerAPI } from '../../api';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    completed: {
      label: 'Bonus Credited',
      icon: CheckCircle,
      className: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    },
    signed_up: {
      label: 'Awaiting Payment',
      icon: Clock,
      className: 'bg-amber-100 text-amber-700 border-amber-200'
    },
    payment_made: {
      label: 'Processing',
      icon: Loader2,
      className: 'bg-blue-100 text-blue-700 border-blue-200'
    }
  };
  
  const config = statusConfig[status] || statusConfig.signed_up;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${config.className}`}>
      <Icon className={`w-3.5 h-3.5 ${status === 'payment_made' ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, title, value, subtitle, gradient, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`rounded-2xl p-5 ${gradient ? `bg-gradient-to-br ${gradient} text-white` : 'bg-white border border-slate-200'} ${className}`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
        gradient ? 'bg-white/20' : 'bg-slate-100'
      }`}>
        <Icon className={`w-6 h-6 ${gradient ? 'text-white' : 'text-slate-600'}`} />
      </div>
      <div>
        <p className={`text-sm ${gradient ? 'text-white/80' : 'text-slate-500'}`}>{title}</p>
        <p className={`text-2xl sm:text-3xl font-bold ${gradient ? '' : 'text-slate-900'}`}>{value}</p>
      </div>
    </div>
    {subtitle && (
      <p className={`mt-2 text-xs ${gradient ? 'text-white/70' : 'text-slate-500'}`}>{subtitle}</p>
    )}
  </motion.div>
);

// Referral Row Component
const ReferralRow = ({ referral }) => {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-xl gap-3">
      <div className="flex items-center gap-3 sm:gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          referral.status === 'completed' ? 'bg-emerald-100' : 'bg-amber-100'
        }`}>
          <Users className={`w-5 h-5 ${
            referral.status === 'completed' ? 'text-emerald-600' : 'text-amber-600'
          }`} />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-slate-900 truncate">{referral.business_name || 'New Partner'}</p>
          <p className="text-sm text-slate-500">
            Signed up {referral.signed_up_at ? format(parseISO(referral.signed_up_at), 'MMM d, yyyy') : 'recently'}
          </p>
        </div>
      </div>
      
      <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
        <StatusBadge status={referral.status} />
        
        {referral.status === 'completed' ? (
          <div className="flex items-center gap-1.5 text-emerald-600 font-semibold">
            <span>+{referral.credits_earned}</span>
            <Gift className="w-4 h-4" />
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-slate-400 text-sm">
            <span>+{referral.credits_pending}</span>
            <span className="text-xs">pending</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Email Template Preview
const EmailPreview = ({ referrerName, customMessage }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
    <div className="text-xs text-slate-400 mb-2">Email Preview</div>
    <div className="space-y-3">
      <h4 className="font-semibold text-slate-800">🎁 {referrerName} invited you to Nanokart.ai!</h4>
      <p className="text-sm text-slate-600">
        <strong>{referrerName}</strong> thinks you'd love Nanokart.ai - the AI-powered Virtual Try-On platform for fashion businesses.
      </p>
      {customMessage && (
        <div className="bg-emerald-50 border-l-4 border-emerald-400 p-3 rounded-r">
          <p className="text-xs text-emerald-700 font-medium mb-1">Personal message:</p>
          <p className="text-sm text-emerald-800 italic">"{customMessage}"</p>
        </div>
      )}
      <div className="bg-blue-50 rounded-lg p-3 text-center">
        <p className="text-sm text-blue-700 font-medium">🎉 Get 100 FREE Credits on Sign Up!</p>
      </div>
      <div className="text-center">
        <span className="inline-block px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg">
          Accept Invitation & Sign Up →
        </span>
      </div>
    </div>
  </div>
);

const Referral = () => {
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Email invitation states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sentInvitations, setSentInvitations] = useState([]);

  useEffect(() => {
    fetchReferralData();
    fetchInvitations();
  }, []);

  const fetchReferralData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await partnerAPI.getReferralInfo();
      setReferralData(response.data);
    } catch (error) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await partnerAPI.getReferralInvitations();
      setSentInvitations(response.data.invitations || []);
    } catch (error) {
      // Non-critical, ignore errors
    }
  };

  const getReferralLink = () => {
    if (!referralData?.referral_email) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/partners/register?ref=${encodeURIComponent(referralData.referral_email)}`;
  };

  const copyReferralLink = async () => {
    const link = getReferralLink();
    if (!link) {
      toast.error('Referral link not available');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const shareReferralLink = async () => {
    const link = getReferralLink();
    if (!link) {
      toast.error('Referral link not available');
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Nanokart.ai',
          text: `Join Nanokart.ai and start using AI-powered virtual try-on for your business! Use my referral link to sign up and we both benefit:`,
          url: link
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          copyReferralLink();
        }
      }
    } else {
      copyReferralLink();
    }
  };

  const handleSendInvitation = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSendingInvite(true);
    try {
      await partnerAPI.sendReferralInvitation({
        recipient_email: inviteEmail.trim(),
        custom_message: customMessage.trim() || null
      });
      
      toast.success(`Invitation sent to ${inviteEmail}!`);
      setInviteEmail('');
      setCustomMessage('');
      setShowInviteModal(false);
      fetchInvitations();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send invitation';
      toast.error(message);
    } finally {
      setSendingInvite(false);
    }
  };

  const referrerName = referralData?.referral_email?.split('@')[0] || 'You';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  const referralLink = getReferralLink();
  const completedReferrals = referralData?.referrals?.filter(r => r.status === 'completed') || [];
  const pendingReferrals = referralData?.referrals?.filter(r => r.status === 'signed_up') || [];

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="referral-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Referral Program</h1>
          <p className="text-slate-600 text-sm mt-1">Invite partners and earn 200 credits for each successful referral</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            data-testid="invite-by-email-btn"
          >
            <Mail className="w-4 h-4" />
            Invite by Email
          </button>
          <button
            onClick={() => fetchReferralData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={Coins}
          title="Credits Earned"
          value={referralData?.referral_credits_earned || 0}
          subtitle="From completed referrals"
          gradient="from-emerald-500 to-green-600"
        />
        <StatsCard
          icon={Users}
          title="Total Referrals"
          value={referralData?.referral_count || 0}
          subtitle="Partners who signed up"
        />
        <StatsCard
          icon={CheckCircle}
          title="Completed"
          value={completedReferrals.length}
          subtitle="Made first payment"
        />
        <StatsCard
          icon={Mail}
          title="Invitations Sent"
          value={sentInvitations.length}
          subtitle="Email invitations"
        />
      </div>

      {/* Invite by Email Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl border border-purple-100 p-5 sm:p-6"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-100 rounded-xl">
              <Mail className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Invite by Email</h3>
              <p className="text-sm text-slate-600">Send personalized invitations directly to potential partners</p>
            </div>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            Compose Invitation
          </button>
        </div>
        
        {/* Quick invite form (inline) */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Enter email address to invite..."
              className="w-full px-4 py-3 border border-purple-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
              data-testid="quick-invite-email"
            />
          </div>
          <button
            onClick={handleSendInvitation}
            disabled={!inviteEmail.trim() || sendingInvite}
            className="px-5 py-3 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="quick-invite-btn"
          >
            {sendingInvite ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Invite
              </>
            )}
          </button>
        </div>
        
        <p className="text-xs text-purple-600 mt-2">
          💡 Tip: Click "Compose Invitation" to add a personal message
        </p>
      </motion.div>

      {/* How it Works - Updated */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl border border-blue-100 p-5 sm:p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-600" />
          How It Works
        </h3>
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
              1
            </div>
            <div>
              <p className="font-medium text-slate-900">Invite Partners</p>
              <p className="text-sm text-slate-600">Send email invitations or share your unique referral link</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
              2
            </div>
            <div>
              <p className="font-medium text-slate-900">They Sign Up & Pay</p>
              <p className="text-sm text-slate-600">When they register and make their <span className="font-semibold text-blue-600">first payment</span></p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm shrink-0">
              <Gift className="w-4 h-4" />
            </div>
            <div>
              <p className="font-medium text-slate-900">You Earn Credits</p>
              <p className="text-sm text-slate-600">Get <span className="font-bold text-emerald-600">200 free credits</span> automatically!</p>
            </div>
          </div>
        </div>
        
        {/* Important Note */}
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
          <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Important:</strong> You'll receive 200 credits only after your referral makes their first payment (credit recharge or subscription).
          </p>
        </div>
      </motion.div>

      {/* Referral Link Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6"
      >
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Link2 className="w-5 h-5 text-blue-600" />
          Your Referral Link
        </h3>
        
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={referralLink || 'Loading...'}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-sm font-mono pr-24"
              data-testid="referral-link-input"
            />
            <button
              onClick={copyReferralLink}
              disabled={!referralLink}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-1.5 disabled:opacity-50"
              data-testid="copy-referral-link-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
          <button
            onClick={shareReferralLink}
            disabled={!referralLink}
            className="px-5 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="share-referral-btn"
          >
            <Share2 className="w-5 h-5" />
            Share
          </button>
        </div>
      </motion.div>

      {/* Referral Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
      >
        <div className="p-5 border-b border-slate-100">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Referral Activity
          </h3>
          <p className="text-sm text-slate-500 mt-1">Track the status of your referrals</p>
        </div>

        {referralData?.referrals?.length > 0 ? (
          <div className="p-4 space-y-3">
            {referralData.referrals.map((referral, index) => (
              <ReferralRow key={referral.partner_id || index} referral={referral} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 px-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-700 font-medium mb-2">No referrals yet</p>
            <p className="text-sm text-slate-500 mb-4">Send an email invitation or share your referral link to start earning</p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Mail className="w-4 h-4" />
              Send First Invitation
            </button>
          </div>
        )}
      </motion.div>

      {/* Sent Invitations */}
      {sentInvitations.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
        >
          <div className="p-5 border-b border-slate-100">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Inbox className="w-5 h-5 text-purple-600" />
              Sent Invitations
            </h3>
            <p className="text-sm text-slate-500 mt-1">Email invitations you've sent</p>
          </div>
          <div className="p-4 space-y-2">
            {sentInvitations.slice(0, 5).map((inv, index) => (
              <div key={inv.invitation_id || index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-700">{inv.recipient_email_masked || inv.recipient_email}</span>
                </div>
                <span className="text-xs text-slate-500">
                  {inv.sent_at ? format(parseISO(inv.sent_at), 'MMM d, yyyy') : '-'}
                </span>
              </div>
            ))}
            {sentInvitations.length > 5 && (
              <p className="text-xs text-slate-500 text-center pt-2">
                +{sentInvitations.length - 5} more invitations
              </p>
            )}
          </div>
        </motion.div>
      )}

      {/* Status Legend */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-slate-50 rounded-xl p-4 border border-slate-200"
      >
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Status Guide</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="flex items-start gap-2">
            <StatusBadge status="signed_up" />
            <p className="text-xs text-slate-600">Partner signed up but hasn't made a payment yet</p>
          </div>
          <div className="flex items-start gap-2">
            <StatusBadge status="completed" />
            <p className="text-xs text-slate-600">Partner made first payment - you earned 200 credits!</p>
          </div>
        </div>
      </motion.div>

      {/* Pro Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-5 sm:p-6"
      >
        <h3 className="text-lg font-semibold text-amber-900 mb-3 flex items-center gap-2">
          <Target className="w-5 h-5" />
          Pro Tips to Maximize Referrals
        </h3>
        <ul className="space-y-3 text-sm text-amber-800">
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
            <span><strong>Personalize your message:</strong> Add a custom note when sending email invitations - it increases conversion!</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
            <span>Share your link on LinkedIn and Twitter to reach more potential partners</span>
          </li>
          <li className="flex items-start gap-2">
            <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
            <span>Remind pending referrals to add credits or subscribe to unlock your bonus!</span>
          </li>
        </ul>
      </motion.div>

      {/* Earnings Banner */}
      {((referralData?.referral_credits_earned || 0) > 0 || pendingReferrals.length > 0 || sentInvitations.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl p-5 sm:p-6 text-white"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <PartyPopper className="w-6 h-6" />
                <h3 className="text-lg font-semibold">
                  {(referralData?.referral_credits_earned || 0) > 0 ? 'Congratulations!' : 'Your Referral Journey'}
                </h3>
              </div>
              <p className="text-emerald-100">
                {(referralData?.referral_credits_earned || 0) > 0 ? (
                  <>You've earned <span className="font-bold text-white">{referralData?.referral_credits_earned} credits</span> from {completedReferrals.length} successful referral{completedReferrals.length !== 1 ? 's' : ''}!</>
                ) : (
                  <>You've sent <span className="font-bold text-white">{sentInvitations.length}</span> invitation{sentInvitations.length !== 1 ? 's' : ''}. Keep growing your network!</>
                )}
              </p>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-emerald-100 text-xs">Potential earnings</p>
              <p className="text-2xl font-bold">+{(pendingReferrals.length * 200) + (sentInvitations.length * 200)}</p>
              <p className="text-emerald-100 text-xs">
                {pendingReferrals.length > 0 && `${pendingReferrals.length} pending`}
                {pendingReferrals.length > 0 && sentInvitations.length > 0 && ' + '}
                {sentInvitations.length > 0 && `${sentInvitations.length} invited`}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Send Referral Invitation
                </h2>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Email Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Recipient Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="partner@example.com"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="modal-invite-email"
                  />
                </div>
                
                {/* Custom Message */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Personal Message (Optional)
                  </label>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Add a personal touch to your invitation..."
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    data-testid="modal-custom-message"
                  />
                  <p className="text-xs text-slate-500 mt-1">{customMessage.length}/500 characters</p>
                </div>
                
                {/* Preview Toggle */}
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Edit3 className="w-4 h-4" />
                  {showPreview ? 'Hide Preview' : 'Preview Email'}
                </button>
                
                {/* Email Preview */}
                <AnimatePresence>
                  {showPreview && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <EmailPreview 
                        referrerName={referrerName}
                        customMessage={customMessage}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Benefits reminder */}
                <div className="bg-emerald-50 rounded-xl p-4">
                  <p className="text-sm text-emerald-800">
                    <strong>What they get:</strong> 100 free credits on signup
                  </p>
                  <p className="text-sm text-emerald-800">
                    <strong>What you get:</strong> 200 credits when they make their first payment
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendInvitation}
                    disabled={!inviteEmail.trim() || sendingInvite}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="modal-send-btn"
                  >
                    {sendingInvite ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Referral;
